import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";
import {
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Switch,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import LanguageIcon from "@mui/icons-material/Language";
import EmailIcon from "@mui/icons-material/Email";
import PeopleIcon from "@mui/icons-material/People";
import ArticleIcon from "@mui/icons-material/Article";
import BusinessIcon from "@mui/icons-material/Business";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useSnackbar } from "notistack";

const DEFAULT_GLOBE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>`;

// Sanitize hardcoded black fills/strokes to inherit theme color (currentColor)
const processSvgCode = (svg: string) => {
  return svg
    .replace(/fill="(#000000|#000|black)"/gi, 'fill="currentColor"')
    .replace(/stroke="(#000000|#000|black)"/gi, 'stroke="currentColor"');
};

// MUI Fallback Icons when no custom SVG is stored in DB
const ADMIN_DEFAULT_ICONS: Record<string, React.ReactNode> = {
  email: <EmailIcon sx={{ fontSize: 16, color: "#EF4444" }} />,
  friend: <PeopleIcon sx={{ fontSize: 16, color: "#A855F7" }} />,
  newsletter: <ArticleIcon sx={{ fontSize: 16, color: "#EC4899" }} />,
  agency: <BusinessIcon sx={{ fontSize: 16, color: "text.secondary" }} />,
  other: <MoreHorizIcon sx={{ fontSize: 16, color: "text.secondary" }} />,
  default_globe: <LanguageIcon sx={{ fontSize: 16, color: "text.primary" }} />,
};

type SvgSourceMode = "default" | "upload" | "code";

interface Option {
  id: number;
  key: string;
  label: string;
  is_active: boolean;
  svg_code?: string | null;
}

export function AdminDiscoveryOptionManager() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingOption, setEditingOption] = useState<Option | null>(null);
  const [newLabel, setNewLabel] = useState("");
  const [svgMode, setSvgMode] = useState<SvgSourceMode>("default");
  const [svgCode, setSvgCode] = useState("");
  const [fileName, setFileName] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["adminOnboardingOptions"],
    queryFn: () => authenticateAndFetchData("/admin/onboarding-options/"),
    refetchOnWindowFocus: false,
    retry: retryFn,
  });

  const options: Option[] = data?.data?.options || [];

  const actionMutation = useMutation({
    mutationKey: ["actionMutation"],
    mutationFn: (payload: { url: string; data?: any }) =>
      authenticateAndPostData(payload.url, payload.data || {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminOnboardingOptions"] });
      queryClient.invalidateQueries({ queryKey: ["adminOnboardingGraphData"] });
      handleCloseDialog();
      enqueueSnackbar("Option updated successfully!", { variant: "success" });
    },
    onError: () =>
      enqueueSnackbar("Failed to process request", { variant: "error" }),
  });

  const handleOpenAddDialog = () => {
    setEditingOption(null);
    setNewLabel("");
    setSvgMode("default");
    setSvgCode("");
    setFileName("");
    setIsDialogOpen(true);
  };

  const handleOpenEditDialog = (option: Option) => {
    setEditingOption(option);
    setNewLabel(option.label);
    setSvgCode(option.svg_code || "");
    setSvgMode(option.svg_code ? "code" : "default");
    setFileName("");
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setEditingOption(null);
    setNewLabel("");
    setSvgMode("default");
    setSvgCode("");
    setFileName("");
    setIsDialogOpen(false);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".svg")) {
      enqueueSnackbar("Please upload a valid .svg file", { variant: "warning" });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const rawText = event.target?.result as string;
      setSvgCode(rawText);
      setFileName(file.name);
    };
    reader.readAsText(file);
  };

  const handleSaveOption = () => {
    if (!newLabel.trim()) return;

    let finalSvgCode: string | null = null;
    if (svgMode === "default") {
      finalSvgCode = DEFAULT_GLOBE_SVG;
    } else if (svgMode === "upload" || svgMode === "code") {
      finalSvgCode = svgCode.trim() ? processSvgCode(svgCode.trim()) : DEFAULT_GLOBE_SVG;
    }

    actionMutation.mutate({
      url: "/admin/onboarding-options/",
      data: {
        ...(editingOption ? { id: editingOption.id } : {}),
        label: newLabel.trim(),
        svg_code: finalSvgCode,
      },
    });
  };

  const handleSeedDefaults = () => {
    actionMutation.mutate({
      url: "/admin/onboarding-options/",
      data: { seed_defaults: true },
    });
  };

  const handleToggleActive = (optionId: number) => {
    actionMutation.mutate({
      url: "/admin/onboarding-options/toggle/",
      data: { option_id: optionId },
    });
  };

  const renderOptionIcon = (opt: Option) => {
    if (opt.svg_code && opt.svg_code.trim().length > 0) {
      return (
        <Box
          component="span"
          sx={{
            width: 16,
            height: 16,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: opt.is_active ? "primary.main" : "text.primary",
            "& > svg": { width: 16, height: 16, display: "block" },
          }}
          dangerouslySetInnerHTML={{ __html: processSvgCode(opt.svg_code) }}
        />
      );
    }

    // Render fallback icon if SVG code is null or empty
    return (
      ADMIN_DEFAULT_ICONS[opt.key] ||
      ADMIN_DEFAULT_ICONS[opt.label.toLowerCase()] || <LanguageIcon sx={{ fontSize: 16, color: "text.primary" }} />
    );
  };

  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={20} color="primary" />
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Discovery Source Options Manager
        </Typography>
        {options.length === 0 && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleSeedDefaults}
            disabled={actionMutation.isPending}
          >
            Seed Default Options
          </Button>
        )}
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={1.25} alignItems="center" sx={{ mb: 2 }}>
        {options.map((opt) => (
          <Box
            key={opt.id}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              pl: 1.75,
              pr: 1,
              py: 0.6,
              borderRadius: "9999px",
              border: "1px solid",
              borderColor: opt.is_active
                ? "primary.main"
                : (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.12)"
                      : "divider",
              bgcolor: (theme) =>
                opt.is_active
                  ? theme.palette.mode === "dark"
                    ? "rgba(25, 118, 210, 0.08)"
                    : "rgba(25, 118, 210, 0.04)"
                  : "transparent",
              color: opt.is_active
                ? (theme) =>
                    theme.palette.mode === "dark" ? "primary.light" : "primary.main"
                : "text.primary",
              userSelect: "none",
              transition: "all 0.2s ease-in-out",
            }}
          >
            {renderOptionIcon(opt)}

            <Typography
              variant="body2"
              sx={{
                fontSize: "0.875rem",
                fontWeight: opt.is_active ? 600 : 500,
              }}
            >
              {opt.label}
            </Typography>

            <Tooltip title="Edit Option">
              <IconButton
                size="small"
                onClick={() => handleOpenEditDialog(opt)}
                sx={{ p: 0.2, ml: 0.5, color: "text.secondary" }}
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>

            <Switch
              size="small"
              checked={opt.is_active}
              onChange={() => handleToggleActive(opt.id)}
              sx={{
                width: 34,
                height: 20,
                p: 0,
                "& .MuiSwitch-switchBase": {
                  p: 0.25,
                  "&.Mui-checked": {
                    transform: "translateX(14px)",
                    color: "#ffffff",
                    "& + .MuiSwitch-track": {
                      backgroundColor: "primary.main",
                      opacity: 1,
                    },
                  },
                },
                "& .MuiSwitch-thumb": {
                  width: 14,
                  height: 14,
                  boxShadow: "none",
                },
                "& .MuiSwitch-track": {
                  borderRadius: 10,
                  opacity: 1,
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.15)",
                },
              }}
            />
          </Box>
        ))}
      </Stack>

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={handleOpenAddDialog}
        variant="outlined"
        color="primary"
        sx={{ borderRadius: "8px" }}
      >
        Add Custom Option
      </Button>

      <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editingOption ? "Edit Discovery Option" : "Add Custom Discovery Option"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            <TextField
              label="Option Label"
              placeholder="e.g. TikTok"
              fullWidth
              size="small"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              autoFocus
            />

            <FormControl fullWidth size="small">
              <InputLabel id="svg-option-type-label">Icon Source</InputLabel>
              <Select
                labelId="svg-option-type-label"
                label="Icon Source"
                value={svgMode}
                onChange={(e) => {
                  setSvgMode(e.target.value as SvgSourceMode);
                  setSvgCode("");
                  setFileName("");
                }}
              >
                <MenuItem value="default">Default Icon (Globe)</MenuItem>
                <MenuItem value="upload">Upload SVG File</MenuItem>
                <MenuItem value="code">Raw SVG Code</MenuItem>
              </Select>
            </FormControl>

            {svgMode === "default" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark" ? "grey.800" : "grey.100",
                }}
              >
                <LanguageIcon sx={{ fontSize: 20, color: "text.primary" }} />
                <Typography variant="caption" color="text.secondary">
                  Will display default Globe icon.
                </Typography>
              </Box>
            )}

            {svgMode === "upload" && (
              <Box>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  startIcon={<UploadFileIcon />}
                  fullWidth
                >
                  {fileName ? fileName : "Choose SVG File"}
                  <input
                    type="file"
                    accept=".svg"
                    hidden
                    onChange={handleFileUpload}
                  />
                </Button>
                {fileName && (
                  <FormHelperText color="success">File uploaded successfully.</FormHelperText>
                )}
              </Box>
            )}

            {svgMode === "code" && (
              <TextField
                label="SVG Markup Code"
                placeholder='<svg viewBox="0 0 24 24">...</svg>'
                multiline
                rows={4}
                fullWidth
                size="small"
                value={svgCode}
                onChange={(e) => setSvgCode(e.target.value)}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit" size="small">
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveOption}
            disabled={actionMutation.isPending || !newLabel.trim()}
          >
            {actionMutation.isPending
              ? "Saving..."
              : editingOption
              ? "Update Option"
              : "Save Option"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}